const CREDIT_CARD_ITEM_CVV_ID = 'CreditCardValidateCvv';

const { find, replace } = require('lodash');

class CreditCardItemInlineCvvController {
    constructor($element, $sce, $timeout, $q, settings, digitalPayPaymentErrorDisplayService, digitalPayErrorMessageGenerator, digitalPayIframeEventHandler) {
        this.$element = $element;
        this.$sce = $sce;
        this.$timeout = $timeout;
        this.$q = $q;
        this.settings = settings;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
        this.digitalPayIframeEventHandler = digitalPayIframeEventHandler;
    }

    $onInit() {
        this.uniqueId = `${CREDIT_CARD_ITEM_CVV_ID}${this.card.PaymentInstrumentId}`;
        this.creditCardItemCvvDisableMessage = replace(this.settings.digitalPayScreenReaderMessages.creditCardCvvDisableMessage, '{0}', this.card.CardSuffix);
        this.timeoutPeriodInMillseconds = this.settings.digitalPay.inlineCvvIframeTimeoutInMilliseconds;
        this.isLoading = false;
        this.hasLoaded = false;
        this.hasIframeFailed = false;
        this.resolvedSuccessResponse = {};
        this.isValid = false;
        this.resetPromise();
        this.setupListenerCallbacks();
        this.registerEventListeners();
        this.setupIframe();

        // Execute the promise (validate the CVV) before payment, disable place order button
        this.prePaymentActionDefinitions.addAction(this.uniqueId, this.submitVerifyCvv.bind(this));
        this.paymentStateDefinitions.addDisablePaymentState(this.uniqueId, this.creditCardItemCvvDisableMessage);

        // Delay the loading spinner by the timeout period - so it only shows when it's not going to load in time
        this.$timeout(() => {
            if (!this.hasLoaded) {
                this.isLoading = true;
            }
        }, this.settings.digitalPay.inlineCvvIframeSpinnerDelayInMilliseconds);
    }

    /**
     * Implies that the user no longer wants to make a payment using this particular credit card, remove all related hooks
     */
    $onDestroy() {
        this.prePaymentActionDefinitions.removeAction(this.uniqueId);
        this.paymentStateDefinitions.removeDisablePaymentState(this.uniqueId);
        this.disposeEventListeners();
        this.digitalPayIframeEventHandler.releaseIframeControl(this.uniqueId);
    }

    setupListenerCallbacks() {
        this.payFrameLoadedCallback = () => {
            this.$timeout(() => {
                this.hasLoaded = true;
                this.isLoading = false;

                // Cancel the timer and let the view know the iframe was successfully loaded
                this.$timeout.cancel(this.iframeLoadTimeoutPromise);
            });
        };

        this.payFrameErrorCallback = (response) => {
            if (response.code === this.settings.digitalPayErrorCodes.creditCardIframeSucceededPreviously
                || response.code === this.settings.digitalPayErrorCodes.creditCardCaptureFormExpired
                || response.code === this.settings.digitalPayErrorCodes.creditCardIframeUnableToProcessRequest) {
                this.paymentStateDefinitions.removeDisablePaymentState(this.uniqueId);
                this.submitIframeDeferred.resolve(this.resolvedSuccessResponse);
                this.refreshCreditCards();
                this.resetPromise();
                return;
            } else if (response.code === this.settings.digitalPayErrorCodes.paymentInstrumentNotFound
                    || response.code === this.settings.digitalPayErrorCodes.creditCardCaptureFormAuthentication) {
                this.refreshCreditCards();
            }

            // Any error responses from this point need to be treated as a payment error
            const suffix = this.card.CardSuffix;
            const isSplitPayment = this.digitalPayErrorMessageGenerator.getPaymentErrorMode();
            const messageTemplate = isSplitPayment
                ? this.settings.digitalPayError.creditCardSavedCardSplitPaymentFailure
                : this.settings.digitalPayError.creditCardSavedCardPaymentFailure;
            const errorMessage = this.digitalPayErrorMessageGenerator.formatErrorMessage(messageTemplate, [suffix]);

            this.digitalPayPaymentErrorDisplayService.pushToPaymentErrorBreakdown(errorMessage);
            this.submitIframeDeferred.reject({ id: this.uniqueId, errorMessage });
            this.onErroredCvv({ id: this.card.PaymentInstrumentId });
            this.resetPromise();
        };

        this.payFrameSuccessCallback = (response) => {
            this.resolvedSuccessResponse = {
                paymentInstrumentId: response.paymentInstrumentId,
                stepUpToken: response.stepUpToken,
            };

            this.submitIframeDeferred.resolve(this.resolvedSuccessResponse);
            this.resetPromise();
        };

        this.payFrameValidCallback = () => {
            this.$timeout(() => {
                this.isValid = true;
                this.paymentStateDefinitions.removeDisablePaymentState(this.uniqueId);
                if (this.onValidCvv) {
                    this.onValidCvv();
                }
            });
        };

        this.payFrameInvalidCallback = () => {
            this.$timeout(() => {
                this.isValid = false;
                this.paymentStateDefinitions.addDisablePaymentState(this.uniqueId, this.creditCardItemCvvDisableMessage);
            });
        };

        this.payFrameFocusGainCallback = () => {
            this.$timeout(() => {
                this.digitalPayPaymentErrorDisplayService.clearGlobalError();
                if (this.onCvvFocus) {
                    this.onCvvFocus();
                }
            });
        };

        this.payFrameFocusLostCallback = (response) => {
            this.$timeout(() => {
                if (this.isValid || response.allFieldsEmpty) {
                    this.onValidCvv();
                } else {
                    this.onInvalidCvv();
                }
            });
        };
    }

    registerEventListeners() {
        this.digitalPayIframeEventHandler.on('pay-frame-loaded', this.payFrameLoadedCallback, this.uniqueId);
        this.digitalPayIframeEventHandler.on('pay-frame-error', this.payFrameErrorCallback, this.uniqueId);
        this.digitalPayIframeEventHandler.on('pay-frame-success', this.payFrameSuccessCallback, this.uniqueId);
        this.digitalPayIframeEventHandler.on('pay-frame-valid', this.payFrameValidCallback, this.uniqueId);
        this.digitalPayIframeEventHandler.on('pay-frame-invalid', this.payFrameInvalidCallback, this.uniqueId);
        this.digitalPayIframeEventHandler.on('pay-frame-focus-gain', this.payFrameFocusGainCallback, this.uniqueId);
        this.digitalPayIframeEventHandler.on('pay-frame-focus-lost', this.payFrameFocusLostCallback, this.uniqueId);
    }

    disposeEventListeners() {
        this.digitalPayIframeEventHandler.off('pay-frame-loaded', this.payFrameLoadedCallback);
        this.digitalPayIframeEventHandler.off('pay-frame-error', this.payFrameErrorCallback);
        this.digitalPayIframeEventHandler.off('pay-frame-success', this.payFrameSuccessCallback);
        this.digitalPayIframeEventHandler.off('pay-frame-valid', this.payFrameValidCallback);
        this.digitalPayIframeEventHandler.off('pay-frame-invalid', this.payFrameInvalidCallback);
        this.digitalPayIframeEventHandler.off('pay-frame-focus-gain', this.payFrameFocusGainCallback);
        this.digitalPayIframeEventHandler.off('pay-frame-focus-lost', this.payFrameFocusLostCallback);
    }

    resetPromise() {
        // Reset promise (for next time)
        this.submitIframeDeferred = this.$q.defer();
    }

    submitVerifyCvv() {
        this.digitalPayIframeEventHandler.emit(this.$element.find(`#${this.uniqueId}`)[0], { eventId: 'pay-frame-submit' });
        this.digitalPayIframeEventHandler.setIframeControl(this.uniqueId);
        return this.submitIframeDeferred.promise;
    }

    setupIframe(suppressTimer = false) {
        this.iframeUrl = this.$sce.trustAsResourceUrl(this.card.StepUp.Url);

        // Setup a timer to wait for the iframe to load
        if (!suppressTimer) {
            this.iframeLoadTimeoutPromise = this.$timeout(() => {
                this.hasIframeFailed = true;
                this.setHasLoadFailure();
            }, this.timeoutPeriodInMillseconds);
        }
    }

    $onChanges(changes) {
        // Error handling
        if (changes.paymentErrorStates && changes.paymentErrorStates.currentValue && changes.paymentErrorStates.currentValue.length) {
            const creditCardError = find(changes.paymentErrorStates.currentValue, error =>
                error.state.PaymentInstrumentType === this.settings.digitalPayPaymentTypes.creditCardType &&
                error.state.PaymentInstrumentId === this.card.PaymentInstrumentId);

            // Step-up token update url
            if (creditCardError && creditCardError.state.ErrorCode === this.settings.digitalPayErrorCodes.creditCardStepUpTokenRequired) {
                this.card.StepUp = creditCardError.state.StepUp;
                // We must suppress the timeout timer as the iframe url comes from the cvv dialog (which disables all other iframe events)
                // This has a possible edge case where this iframe fails to load
                this.setupIframe(true);
            }
        }
    }
}

CreditCardItemInlineCvvController.$inject = ['$element', '$sce', '$timeout', '$q', 'Settings', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayErrorMessageGenerator', 'DigitalPayIframeEventHandler'];

const creditCardItemInlineCvvComponentDefinition = {
    controller: CreditCardItemInlineCvvController,
    template: require('./credit-card-item-inline-cvv.component.html').trim(),
    bindings: {
        card: '<',
        paymentErrorStates: '<',
        prePaymentActionDefinitions: '<',
        paymentStateDefinitions: '<',
        setHasLoadFailure: '&',
        refreshCreditCards: '&',
        onInvalidCvv: '&?',
        onValidCvv: '&?',
        onCvvFocus: '&?',
        onErroredCvv: '&',
    },
};

module.exports = {
    CreditCardItemInlineCvvController,
    creditCardItemInlineCvvComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/creditcard/credit-card-item-inline-cvv.component.js